/* qp-shipping.js — the Shipping floating-modal controller + the
   TRAVELING WAYPOINT dot driver (PM R2).

   Drives snippets/qp-shipping-modal.liquid, which renders on the D.SHELL
   contract (assets/qp-shell.css). Mirrors the mobile-sheet double-rAF /
   focus-trap / Esc / scrim-click pattern already locked in qp-nav.js so
   every shell on the site opens and closes the same way.

   TRIGGER CONTRACT (documented per the wave-4 brief — PDP/cart wiring is
   a later wave, this just needs to be clean to consume):
     any element carrying the boolean attribute  data-qp-shipping-modal
     opens the shell on click, event-delegated at the document, so a
     trigger can be added anywhere (buy-box, cart line, footer link)
     without this file needing to change or re-bind.
     ALSO (PM, progressive enhancement): any  a[href*="#shipping-overview"]
     link opens the shell in place when one exists on the page and we are
     not already on /pages/shipping — otherwise the link navigates
     naturally. Pair with the "Shipping popup available site-wide" theme
     setting (layout/theme.liquid renders a bare shell on every page).

   TRAVELING DOT (designer-amended register): one .qp-shipping__rail-dot
   per .qp-shipping__rail-wrap (page master rail + popup rail). Loop:
   rest 2400ms firing ONE halo pulse -> travel 600ms translateY down the
   connector -> repeat; hold 3600ms at the last stage, 300ms fade-out,
   instant (transition-less) reset to stage 01, 300ms fade-in — the dot
   NEVER animates upwards. ~21s full cycle, infinite, but paused via
   IntersectionObserver whenever the rail is off-viewport (a hidden popup
   is off-viewport, so its dot pauses for free). prefers-reduced-motion
   (or a missing IntersectionObserver): the dot parks statically at
   stage 01 — no pulse, no travel, no timers. Decorative only: the dot
   reads nothing from and writes nothing to order/cart state.

   IDLE PREFETCH (PM: kill the cold-open content flash): a bare shell's
   SOT sync (below) used to be strictly lazy — first trigger click opened
   with baked defaults, then visibly swapped a beat later. Any page with
   a bare shell now also warms the same cache during browser idle time,
   before any interaction, so the swap has usually already landed by the
   time a visitor clicks. Data cost (PM-accepted): one HTML fetch per
   visitor per ~10-minute session (the cache TTL) on any shell-bearing
   page — the price of eliminating the drift.

   DISPLAY-ONLY: no cart/line-item/pricing reads or writes happen here. */
(function () {
  'use strict';

  if (window.__qpShippingLoaded) return;
  window.__qpShippingLoaded = true;

  var reducedMotion = window.matchMedia
    ? window.matchMedia('(prefers-reduced-motion: reduce)')
    : { matches: false };

  var hideTimer = null;
  var lastTrigger = null;

  function shellRoot() {
    return document.querySelector('[data-qp-shipping-shell]');
  }

  function open(trigger) {
    var root = shellRoot();
    if (!root) return;
    clearTimeout(hideTimer);
    lastTrigger = trigger || null;
    root.hidden = false;
    root.setAttribute('aria-hidden', 'false');
    /* double rAF so the transform transition runs from the closed state
       — same reasoning as qp-nav.js's mobile sheet. */
    requestAnimationFrame(function () {
      requestAnimationFrame(function () {
        root.classList.add('is-open');
      });
    });
    document.body.classList.add('qp-scroll-lock');
    if (trigger) trigger.setAttribute('aria-expanded', 'true');
    var closeBtn = root.querySelector('[data-qp-shell-close]');
    if (closeBtn) closeBtn.focus();
    root.addEventListener('keydown', trap);
  }

  function close() {
    var root = shellRoot();
    if (!root || root.hidden) return;
    root.classList.remove('is-open');
    root.setAttribute('aria-hidden', 'true');
    root.removeEventListener('keydown', trap);
    document.body.classList.remove('qp-scroll-lock');
    var delay = reducedMotion.matches ? 0 : 240;
    clearTimeout(hideTimer);
    hideTimer = setTimeout(function () {
      if (!root.classList.contains('is-open')) root.hidden = true;
    }, delay);
    if (lastTrigger) {
      lastTrigger.setAttribute('aria-expanded', 'false');
      lastTrigger.focus();
    }
  }

  function trap(e) {
    if (e.key !== 'Tab') return;
    var root = shellRoot();
    if (!root) return;
    var focusables = root.querySelectorAll(
      'a[href], button:not([disabled]), input, select, [tabindex]:not([tabindex="-1"])'
    );
    if (!focusables.length) return;
    var first = focusables[0];
    var last = focusables[focusables.length - 1];
    if (e.shiftKey && document.activeElement === first) {
      e.preventDefault();
      last.focus();
    } else if (!e.shiftKey && document.activeElement === last) {
      e.preventDefault();
      first.focus();
    }
  }

  document.addEventListener('click', function (e) {
    var trigger = e.target.closest('[data-qp-shipping-modal]');
    if (trigger) {
      e.preventDefault();
      /* cache check runs BEFORE open() (hoisted function declaration —
         defined below) so a warm cache (idle-prefetched or synced by an
         earlier page this session) swaps the panel synchronously before
         the shell is ever shown — never a frame of stale defaults. A
         cold cache just kicks off the fetch here as the safety net;
         open() proceeds immediately either way. */
      syncBareShell();
      open(trigger);
      return;
    }

    /* ANCHOR INTERCEPTION (PM): any link targeting the shipping page's
       overview anchor (announcement banner, rich text, nav) opens the
       popup in place when a [data-qp-shipping-shell] is on the page —
       same open path and same SOT bare-fetch as the data-attribute
       triggers. No shell on the page (merchant left the site-wide
       setting off), or already ON /pages/shipping (the page IS the
       content): do nothing and let the link navigate naturally. */
    var anchor = e.target.closest('a[href*="#shipping-overview"]');
    if (anchor) {
      if (shellRoot() && window.location.pathname.indexOf('/pages/shipping') === -1) {
        e.preventDefault();
        syncBareShell(); /* same synchronous-cache-first ordering as above */
        open(anchor);
      }
      return;
    }

    var root = shellRoot();
    if (!root || root.hidden) return;
    if (
      e.target.closest('[data-qp-shell-close]') ||
      e.target.closest('[data-qp-shell-scrim]')
    ) {
      close();
    }
  });

  document.addEventListener('keydown', function (e) {
    if (e.key !== 'Escape') return;
    var root = shellRoot();
    if (root && !root.hidden) close();
  });

  /* soft support hand-off, bound INSIDE the shell with stopPropagation —
     same launcher pattern as sections/cart-template.liquid / qp-nav.js —
     so this never double-fires against any other [data-qp-open-chat]
     delegate elsewhere on the page. */
  document.addEventListener(
    'click',
    function (e) {
      var btn = e.target.closest('[data-qp-open-chat]');
      if (!btn || !btn.closest('[data-qp-shipping-shell]')) return;
      e.stopPropagation();
      close();
      var launcher =
        document.querySelector('#qpu-launcher') ||
        document.querySelector('#tidio-chat iframe, #tidio-chat-iframe');
      if (launcher && typeof launcher.click === 'function') launcher.click();
    },
    true
  );

  /* ---- traveling waypoint dot ------------------------------------------
     One state machine per rail. All motion values land as inline
     transform/opacity; durations/easing live in qp-shipping.css. */

  var REST_MS = 2400;
  var TRAVEL_MS = 600;
  var HOLD_LAST_MS = 3600;
  var FADE_MS = 300;

  function destroyRailDot(wrap) {
    if (wrap.__qpRailDot && typeof wrap.__qpRailDot.destroy === 'function') {
      wrap.__qpRailDot.destroy();
    }
    wrap.__qpRailDot = null;
  }

  function initRailDot(wrap) {
    /* re-init safe: tear down any prior state machine for this wrap
       first so a content swap can never double-run timers */
    destroyRailDot(wrap);

    var dot = wrap.querySelector('.qp-shipping__rail-dot');
    /* master rail stages OR the paint pre-rail's (PM amended the
       one-traveler ceiling to two per page, 2026-07-12) */
    var stages = wrap.querySelectorAll('.qp-shipping__rail-stage, .qp-shipping__paint-stage');
    if (!dot || stages.length < 2) return;

    /* align the dot with a stage's index line, relative to the wrap
       (the wrap is the offsetParent — position: relative in CSS) */
    function yFor(i) {
      return stages[i].offsetTop + 6;
    }

    /* transition-less placement — the only way the dot ever moves UP */
    function park(i) {
      dot.classList.add('is-teleporting');
      dot.style.transform = 'translateY(' + yFor(i) + 'px)';
      void dot.offsetWidth; /* flush so the jump never animates */
      dot.classList.remove('is-teleporting');
    }

    park(0);

    /* static registers: reduced motion, or no IntersectionObserver (the
       loop is viewport-gated by contract — without the observer the dot
       simply rests at stage 01; also keeps headless test runs timer-free) */
    if (reducedMotion.matches) return;
    if (typeof IntersectionObserver !== 'function') return;

    var idx = 0;
    var timer = null;
    var started = false;
    var visible = false;
    var pending = null;
    var dead = false;

    function schedule(ms, fn) {
      if (dead) return;
      if (!visible) {
        pending = function () {
          schedule(ms, fn);
        };
        return;
      }
      timer = setTimeout(fn, ms);
    }

    function pulse() {
      dot.classList.remove('is-pulsing');
      void dot.offsetWidth; /* restart the one-shot halo animation */
      dot.classList.add('is-pulsing');
    }

    function rest() {
      pulse();
      if (idx === stages.length - 1) {
        schedule(HOLD_LAST_MS, fadeReset);
      } else {
        schedule(REST_MS, travel);
      }
    }

    function travel() {
      idx += 1;
      /* downward only — yFor(idx) is always below the previous stage */
      dot.style.transform = 'translateY(' + yFor(idx) + 'px)';
      schedule(TRAVEL_MS, rest);
    }

    function fadeReset() {
      dot.classList.add('is-faded');
      schedule(FADE_MS, function () {
        idx = 0;
        park(0);
        dot.classList.remove('is-faded');
        schedule(FADE_MS, rest);
      });
    }

    var io = new IntersectionObserver(function (entries) {
      for (var i = 0; i < entries.length; i++) {
        if (entries[i].isIntersecting) {
          if (visible) continue;
          visible = true;
          if (!started) {
            started = true;
            rest();
          } else if (pending) {
            var resume = pending;
            pending = null;
            resume();
          }
        } else if (visible) {
          visible = false;
          clearTimeout(timer);
          if (started && !pending) {
            /* re-enter the current stage's rest on resume */
            pending = function () {
              rest();
            };
          }
        }
      }
    });
    io.observe(wrap);

    wrap.__qpRailDot = {
      destroy: function () {
        dead = true;
        pending = null;
        clearTimeout(timer);
        io.disconnect();
      }
    };
  }

  function initTravelingDots() {
    var wraps = document.querySelectorAll('.qp-shipping__rail-wrap');
    for (var i = 0; i < wraps.length; i++) initRailDot(wraps[i]);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initTravelingDots);
  } else {
    initTravelingDots();
  }

  /* ---- bare-popup SOT sync (PM: the popup pulls the Shipping page as
     source of truth) ------------------------------------------------------
     A shell rendered WITHOUT params (the cart page's bare render) carries
     data-qp-modal-bare and initially shows the snippet's baked defaults.
     On the FIRST trigger click we open immediately with those defaults
     and, in parallel, fetch /pages/shipping — the shipping page renders
     the same shell WITH its live section settings, so its markup IS the
     SOT rendering. We swap the local shell's PANEL CONTENT only (the
     shell element, its open/close state, focus trap, and delegation all
     stay), re-init the swapped rail's traveling dot, and drop the flag.

     Resilience: any failure (no fetch API, non-200, timeout, missing or
     malformed shell in the response) keeps the baked defaults silently —
     the defaults ARE the fallback. Success is cached in sessionStorage
     (~10 min TTL) so repeat opens and other pages don't refetch. The
     shipping page's own shell never carries the flag, so this never
     fetches there. */

  var SYNC_URL = '/pages/shipping';
  var SYNC_CACHE_KEY = 'qp:shipping-modal:sot';
  var SYNC_TTL_MS = 600000; /* ~10 min */
  var SYNC_TIMEOUT_MS = 5000;
  var syncState = 'idle'; /* idle | inflight | done | failed */

  function syncCacheRead() {
    try {
      var raw = window.sessionStorage.getItem(SYNC_CACHE_KEY);
      if (!raw) return null;
      var entry = JSON.parse(raw);
      if (!entry || typeof entry.html !== 'string' || !entry.t) return null;
      if (Date.now() - entry.t > SYNC_TTL_MS) return null;
      return entry.html;
    } catch (err) {
      /* parse error invalidates the cache */
      try {
        window.sessionStorage.removeItem(SYNC_CACHE_KEY);
      } catch (err2) {
        /* private mode — nothing to clean */
      }
      return null;
    }
  }

  function syncCacheWrite(html) {
    try {
      window.sessionStorage.setItem(SYNC_CACHE_KEY, JSON.stringify({ t: Date.now(), html: html }));
    } catch (err) {
      /* quota/private mode — skip caching, the swap still happened */
    }
  }

  /* pull the panel's inner content out of the fetched shipping page;
     null on anything unexpected (treated as failure upstream) */
  function extractSotPanel(htmlText) {
    try {
      var parsed = new DOMParser().parseFromString(htmlText, 'text/html');
      var shell = parsed.querySelector('[data-qp-shipping-shell]');
      var panel = shell && shell.querySelector('.qp-shell__panel');
      if (!panel || !panel.querySelector('.qp-shipping__rail-stage')) return null;
      return panel.innerHTML;
    } catch (err) {
      return null;
    }
  }

  function applySotPanel(root, panelHtml) {
    var panel = root.querySelector('.qp-shell__panel');
    if (!panel) return false;

    /* tear down the old rail state BEFORE its nodes leave the DOM so no
       orphaned timers/observers survive the swap */
    var oldWraps = root.querySelectorAll('.qp-shipping__rail-wrap');
    for (var i = 0; i < oldWraps.length; i++) destroyRailDot(oldWraps[i]);

    /* a11y: if the swap lands while the modal is open and focus sits
       inside the panel (e.g. on the close button), restore it to the
       equivalent element instead of letting it fall to <body> */
    var hadFocus = panel.contains(document.activeElement);
    panel.innerHTML = panelHtml;
    if (hadFocus) {
      var closeBtn = panel.querySelector('[data-qp-shell-close]');
      if (closeBtn) closeBtn.focus();
    }

    var wraps = root.querySelectorAll('.qp-shipping__rail-wrap');
    for (var j = 0; j < wraps.length; j++) initRailDot(wraps[j]);

    root.removeAttribute('data-qp-modal-bare');
    syncState = 'done';
    return true;
  }

  function syncBareShell() {
    var root = shellRoot();
    if (!root || !root.hasAttribute('data-qp-modal-bare')) return;
    if (syncState !== 'idle') return;

    var cached = syncCacheRead();
    if (cached && applySotPanel(root, cached)) return;

    if (typeof fetch !== 'function' || typeof DOMParser !== 'function') {
      syncState = 'failed';
      return;
    }

    syncState = 'inflight';
    var controller = typeof AbortController === 'function' ? new AbortController() : null;
    var timeoutId = controller
      ? setTimeout(function () {
          controller.abort();
        }, SYNC_TIMEOUT_MS)
      : null;

    fetch(SYNC_URL, controller ? { signal: controller.signal } : undefined)
      .then(function (res) {
        if (!res || !res.ok) throw new Error('qp-shipping sot: bad status');
        return res.text();
      })
      .then(function (text) {
        var panelHtml = extractSotPanel(text);
        var rootNow = shellRoot();
        if (!panelHtml || !rootNow) throw new Error('qp-shipping sot: no usable shell in response');
        syncCacheWrite(panelHtml);
        applySotPanel(rootNow, panelHtml);
      })
      .catch(function () {
        /* keep the baked defaults silently — the defaults ARE the
           fallback; no retry this page load */
        syncState = 'failed';
      })
      .then(function () {
        if (timeoutId) clearTimeout(timeoutId);
      });
  }

  /* ---- idle prefetch (PM: kill the cold-open content flash) ------------
     The click-time path above is the safety net; this is the common
     case. Warm the SOT cache during browser idle time, before any
     interaction, so the swap has already landed by the time a visitor
     clicks — a cold open no longer shows baked defaults for even a
     beat. Same fetch, same sessionStorage cache/TTL, same silent-failure
     semantics as the click path — this just calls it earlier.

     Guards (reusing what syncBareShell already enforces, so this never
     duplicates logic): no bare shell on the page, or a shell whose flag
     is already gone (e.g. the shipping page's own shell never carries
     it) -> nothing to warm, don't even schedule idle work. The single-
     flight syncState guard means a click that beats the idle callback
     (or vice versa) can never trigger a second fetch. */
  function prefetchBareShell() {
    var root = shellRoot();
    if (!root || !root.hasAttribute('data-qp-modal-bare')) return;

    /* cache already warm (a prior page in this session synced it) ->
       swap right here, synchronously, at init — zero network, and the
       flag is gone before any click is even possible. */
    var cached = syncCacheRead();
    if (cached) {
      applySotPanel(root, cached);
      return;
    }

    var run = function () {
      syncBareShell();
    };
    if (typeof window.requestIdleCallback === 'function') {
      window.requestIdleCallback(run, { timeout: 4000 });
    } else {
      setTimeout(run, 2500);
    }
  }

  prefetchBareShell();
})();
